import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core';
import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { debounce, isNil } from 'lodash';
import {
  useAssignments,
  usePriorityGradingAssignments,
  useUserSections
} from 'sdk';

import { useNewSchoolYearDialog } from 'pages/Teachers/AssignmentsV2/hooks';
import CoachingContent from 'pages/Teachers/Home/components/CoachingContent';
import ColumnContainer from 'pages/Teachers/Home/components/ColumnContainer';
import DashboardHeader from 'pages/Teachers/Home/components/DashboardHeader';
import NavSearchAndFilter from 'pages/Teachers/Home/components/NavSearchAndFilter';
import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import PriorityGradingList from 'pages/Teachers/Home/components/PriorityGradingList';
import SectionsList from 'pages/Teachers/Home/components/SectionsList';
import StartEmptyBox from 'pages/Teachers/Home/components/StartEmptyBox';
import { useNavbar } from 'pages/Teachers/shared';
import { getRenderedSectionName } from 'utils/assignments';
import { formatRelativeTime } from 'utils/date';
import { bootIntercomWidget } from 'utils/integrations/intercom';
import { getClarityUserId } from 'utils/integrations/mixpanel';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { reverse } from 'utils/urls';

import AssignmentDialogContext from 'components/AssignmentDialogContext';
import UpdateGoogleClassroomRostersDialog from 'components/UpdateGoogleClassroomRostersDialog';
import UserContext from 'components/UserContext';

import { ReactComponent as BellIcon } from './assets/bell-icon.svg';
import { ReactComponent as CoachingIcon } from './assets/coaching-icon.svg';
import { ReactComponent as CoachingSmallIcon } from './assets/coaching-small-icon.svg';
import { ReactComponent as GradingIcon } from './assets/grading-icon.svg';
import { ReactComponent as HomeIcon } from './assets/home-icon.svg';
import { ReactComponent as SectionsIcon } from './assets/sections-icon.svg';
import { ReactComponent as StartIcon } from './assets/start-icon.svg';
import styles from './styles.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';
const HOME_PAGE_PRIORITY_GRADING_ENABLED =
  process.env.REACT_APP_HOME_PAGE_PRIORITY_GRADING_ENABLED === 'true';
const HOME_PAGE_DISCUSS_AND_COACHING_ENABLED =
  process.env.REACT_APP_HOME_PAGE_DISCUSS_AND_COACHING_ENABLED === 'true';

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    height: '36px',
    backgroundColor: '#F8F7FF',
    borderRadius: '10px',
    border: '1.5px solid #E0D9F7',
    padding: '4px 12px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#D1C4E9',
      backgroundColor: '#F3F0FF'
    },
    '&:before, &:after': {
      display: 'none'
    }
  },
  selectIcon: {
    color: '#6A61A1',
    right: '4px'
  },
  inputText: {
    color: '#6A61A1',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  placeholder: {
    color: '#7B74AB',
    opacity: 0.8
  },
  menuItemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    maxWidth: '280px'
  }
}));

const Home = () => {
  const [isLogged, setIsLogged] = useState(false);
  const [sectionValue, setSectionValue] = useState('placeholder');
  const [openSection, setOpenSection] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [assignmentsFilter, setAssignmentsFilter] = useState(null);
  const [misconceptionsLastUpdated, setMisconceptionsLastUpdated] = useState();
  const [shouldExpandAssignmentsList, setShouldExpandAssignmentsList] =
    useState(false);
  const [, setTick] = useState(0);

  const history = useHistory();
  const { user } = useContext(UserContext);

  const { data: userSections } = useUserSections({
    params: {}
  });

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20
  });

  const [open, setOpen] = useState(false);

  const { setIsAssignmentDialogOpened } = useContext(AssignmentDialogContext);

  const classes = useStyles();

  const filters = useMemo(
    () => ({
      offset: pagination.offset,
      search: assignmentsFilter,
      section: sectionValue !== 'placeholder' ? sectionValue : null
    }),
    [pagination, assignmentsFilter, sectionValue]
  );

  const { data: trackersOverviewData } = useAssignments({
    params: filters
  });

  const {
    data: priorityGradingAssignments,
    isLoading: isLoadingPriorityGrading,
    refetch: refetchPriorityGrading
  } = usePriorityGradingAssignments({
    params: { offset: 0, limit: 3 }
  });

  const {
    isUpdateGoogleClassroomRostersDialogOpened,
    closeUpdateGoogleClassroomRostersDialog,
    goToGoogleAuthScreen
  } = useNewSchoolYearDialog();

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, []);

  const relativeUpdateTime = misconceptionsLastUpdated
    ? formatRelativeTime({ date: misconceptionsLastUpdated })
    : '';

  useEffect(() => {
    if (!HOME_PAGE_ENABLED) {
      history.push(TEACHER_ASSIGNMENTS_V2_URL);
    }
  }, [history]);

  useEffect(() => {
    if (!isNil(trackersOverviewData) && shouldExpandAssignmentsList) {
      setAssignments((prev) => [...prev, ...trackersOverviewData.results]);
      setShouldExpandAssignmentsList(false);
    } else if (!isNil(trackersOverviewData) && pagination.offset === 0) {
      setAssignments(trackersOverviewData.results);
    }
  }, [trackersOverviewData, shouldExpandAssignmentsList, pagination.offset]);

  useEffect(() => {
    if (!user) {
      return;
    }
    bootIntercomWidget({
      email: user.email,
      name: user.name
    });
  }, [user]);

  const runSearch = useMemo(
    () =>
      debounce((value) => {
        setAssignmentsFilter(value);
        setPagination({
          offset: 0,
          limit: 20
        });
      }, 300),
    []
  );

  const onChange = useCallback(
    (e) => {
      const value = e.target.value;
      setQuery(value);
      runSearch(value);
      if (value === '') {
        setAssignments([]);
        setOpen(false);
      } else {
        setOpen(true);
      }
    },
    [runSearch]
  );

  const handleSelect = useCallback(
    ({ assignment }) => {
      trackMixpanelEvent(user.id, 'Select specific assignment.', {
        feature: 'home page assignment search',
        view: 'home page',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL
      });

      setQuery('');
      history.push({
        pathname: reverse(
          assignment.is_math_assignment
            ? assignment.submitted === 0
              ? TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL
              : TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
            : TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
          {
            trackerInstanceSectionId: assignment.id
          }
        )
      });
      setOpen(false);
    },
    [history, user.id]
  );

  useEffect(() => {
    // 1.5s timeout gives the external Clarity script a moment to load and drop the cookie
    const timer = setTimeout(() => {
      const clarityUserId = getClarityUserId();

      trackMixpanelEvent(user.id, 'Clarity User ID Retrieved', {
        feature: 'home page',
        view: 'home page',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL,
        clarityUserId: clarityUserId || 'Not Available'
      });
    }, 1500);

    // Cleanup the timer on unmount
    return () => clearTimeout(timer);
  }, [user.id]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        !isNil(trackersOverviewData) &&
        !isNil(trackersOverviewData.next)
      ) {
        setPagination((prev) => ({
          ...prev,
          offset: trackersOverviewData.offset + trackersOverviewData.limit
        }));
        setShouldExpandAssignmentsList(true);
      }
    },
    [trackersOverviewData]
  );

  const handleFiltersOpen = useCallback(() => {
    setFiltersOpen((open) => !open);

    trackMixpanelEvent(user.id, 'Filters opened.', {
      feature: 'home page',
      view: 'home page',
      normalizedURL: process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL
    });
  }, [user.id]);

  const handleCreateAssignment = () => {
    trackMixpanelEvent(user.id, 'Create Assignment Button Clicked.', {
      feature: 'home page',
      view: 'home page',
      normalizedURL: process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL
    });

    setIsAssignmentDialogOpened(true);
  };

  const hasPriorityAssignments = priorityGradingAssignments?.count > 0;

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title={'Home'}
          subtitle={
            'Prioritize grading, review patterns, and plan tomorrow’s next steps.'
          }
          Icon={HomeIcon}
        />
      ),
      RightSubheader: (
        <NavSearchAndFilter
          userId={user.id}
          query={query}
          onChange={onChange}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          open={open}
          handleScroll={handleScroll}
          handleFiltersOpen={handleFiltersOpen}
          assignments={assignments}
          handleSelect={handleSelect}
          openSection={openSection}
          setOpenSection={setOpenSection}
          sectionValue={sectionValue}
          setSectionValue={setSectionValue}
          setPagination={setPagination}
          userSections={userSections}
          classes={classes}
          getRenderedSectionName={getRenderedSectionName}
        />
      ),
      isTransparent: true
    }),
    [
      user.id,
      query,
      onChange,
      filtersOpen,
      open,
      handleScroll,
      handleFiltersOpen,
      assignments,
      handleSelect,
      openSection,
      sectionValue,
      userSections,
      classes
    ]
  );

  useNavbar(navConfig);

  return (
    <>
      <div className={styles.container}>
        <DashboardHeader
          userId={user.id}
          setIsLogged={setIsLogged}
          handleCreateAssignment={handleCreateAssignment}
        />

        <div className={styles.grid}>
          {/* COLUMN 1: Priority Grading */}
          <ColumnContainer
            disclaimer={!HOME_PAGE_PRIORITY_GRADING_ENABLED && 'START HERE:'}
            titleDescription=" Priority Grading"
            smallIcon={
              HOME_PAGE_PRIORITY_GRADING_ENABLED ? <BellIcon /> : <StartIcon />
            }
            headerRight={
              isLogged ? <span className={styles.countBadge}></span> : null
            }
            isCommingSoon={false}
          >
            {HOME_PAGE_PRIORITY_GRADING_ENABLED ? (
              isLoadingPriorityGrading ? (
                <PriorityGradingList isLoading={true} />
              ) : hasPriorityAssignments ? (
                <PriorityGradingList
                  initialData={priorityGradingAssignments}
                  refetchData={refetchPriorityGrading}
                />
              ) : (
                <StartEmptyBox
                  stepNumber="STEP 1"
                  description="Launch your first student mission to see priority grading appear here."
                  icon={<GradingIcon />}
                  actionLabel="CREATE ASSIGNMENT"
                  handleCreateAssignment={handleCreateAssignment}
                />
              )
            ) : (
              <StartEmptyBox
                stepNumber="STEP 1"
                description="Launch your first student mission to see priority grading appear here."
                icon={<GradingIcon />}
                actionLabel="CREATE ASSIGNMENT"
                handleCreateAssignment={handleCreateAssignment}
              />
            )}
          </ColumnContainer>

          {/* COLUMN 3: PLC / Coaching */}
          <ColumnContainer
            title=""
            titleDescription="Discuss in PLC / Coaching"
            smallIcon={<CoachingSmallIcon />}
            headerRight={relativeUpdateTime}
          >
            {HOME_PAGE_DISCUSS_AND_COACHING_ENABLED ? (
              <CoachingContent
                setMisconceptionsLastUpdated={setMisconceptionsLastUpdated}
              />
            ) : (
              <StartEmptyBox
                stepNumber="STEP 3"
                description="Once students submit, we will group them by misconception for your next team meeting."
                icon={<CoachingIcon />}
              />
            )}
          </ColumnContainer>
          <ColumnContainer
            titleDescription="Classes"
            smallIcon={<SectionsIcon />}
            headerRight={
              isLogged ? <span className={styles.countBadge}></span> : null
            }
          >
            <SectionsList />
          </ColumnContainer>
        </div>
      </div>
      {isUpdateGoogleClassroomRostersDialogOpened && (
        <UpdateGoogleClassroomRostersDialog
          onSync={goToGoogleAuthScreen}
          onClose={closeUpdateGoogleClassroomRostersDialog}
        />
      )}
    </>
  );
};

export default Home;
